import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import HipchatChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Grid, media, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useTenantData } from '@dev-agents/tenant-context';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import rocketImage from '../../assets/rocket.svg';
import settingsImage from '../../assets/settings.svg';
import { fg } from '../../controllers/feature-gates';
import { useRovoDevWebAnalyticsClient } from '../../controllers/rovo-dev-web-analytics-events';
import { useUserPermData } from '../../utils/user-perm-context';

import type { homePageMainQuery } from './__generated__/homePageMainQuery.graphql';
import { Capabilities } from './capabilities';
import { Card } from './common/card';
import { Feedback } from './feedback';
import { Manage } from './manage';
import messages from './messages';
import { NonProductAdminHomePage } from './non-product-admins-home';
import {
	getConfiguredProviders,
	getRequiredProviders,
	integrations,
	PROVIDER_IDS,
} from './utils/integrations';

const MainContentQuery = graphql`
	query homePageMainQuery($cloudID: ID!) {
		jira {
			devOps {
				configStates(cloudId: $cloudID, providerTypeFilter: [DEVELOPMENT_INFO])
					@optIn(to: "Jira-config-states-by-provider") {
					edges {
						...manage_IntegrationsConfigStates
						...capabilities_IntegrationsConfigStates
						node {
							id
							config {
								edges {
									node {
										status
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

const MainContent = ({ cloudID }: { cloudID: string }) => {
	const { formatMessage } = useIntl();

	const data = useLazyLoadQuery<homePageMainQuery>(MainContentQuery, { cloudID });
	const { jiraAdminPermissions } = useUserPermData();
	const [skipSetupSection, setSkipSetupSection] = useState(false);
	const [hideSetupSection, setHideSetupSection] = useState(false);

	const configStates = Object.fromEntries(
		data.jira?.devOps?.configStates?.edges?.map((edge) => [
			edge?.node?.id,
			edge?.node?.config?.edges?.[0]?.node?.status || 'NOT_CONFIGURED',
		]) || [],
	);

	const integrationsComponent = useMemo(
		() =>
			integrations
				.filter((integration) => getRequiredProviders().includes(integration.id))
				.filter((integration) => {
					if (!fg('rovo-dev-agents-permissions-patch-enabled')) {
						return true;
					}
					if (integration.id === PROVIDER_IDS.BITBUCKET) {
						return jiraAdminPermissions.includes('manage');
					}
					if (
						integration.id === PROVIDER_IDS.GITHUB_PROD ||
						integration.id === PROVIDER_IDS.GITHUB_STG
					) {
						return (
							jiraAdminPermissions.includes('create:app-installation') &&
							jiraAdminPermissions.includes('delete:app-installation') &&
							jiraAdminPermissions.includes('update:app-installation')
						);
					}
					return true;
				})
				.map((integration, index) => (
					<Card
						description={integration.description}
						icon={integration.icon}
						primaryAction={integration.primaryAction}
						secondaryAction={integration.secondaryAction}
						title={integration.title}
						type="suggestion"
						key={`integration-${index}`}
					/>
				)),
		[jiraAdminPermissions],
	);

	const showIntegrationsIncompleteStatus =
		!getConfiguredProviders(configStates).length && !skipSetupSection;

	const { fireUiEvent } = useRovoDevWebAnalyticsClient();

	return (
		<Grid alignItems="center" xcss={responsiveStyles}>
			<Flex alignItems="center" justifyContent="center">
				<Box xcss={homeWrapperStyles}>
					<Stack space="space.300">
						<Box xcss={suggestionsWrapperStyles}>
							<Box
								xcss={
									showIntegrationsIncompleteStatus
										? setupHeadingWrapperStyles
										: mainHeadingWrapperStyles
								}
							>
								<Stack space="space.150" xcss={mainHeadingStyles}>
									<Flex gap="space.100" alignItems="center">
										<Heading size="xxlarge" testId="home-page-heading">
											{showIntegrationsIncompleteStatus
												? formatMessage(messages.setUpRovoDevAgentsHeading)
												: formatMessage(messages.rovoDevAgentsHeading)}
										</Heading>
										<Lozenge appearance="inprogress" isBold>
											{formatMessage(messages.beta)}
										</Lozenge>
									</Flex>

									<Text as="p" testId="home-page-description">
										<Box xcss={homeDescriptionStyles}>
											{formatMessage(messages.homeDescription)}
										</Box>
										<Link
											href="https://rovodevagents-beta.atlassian.net/wiki/external/OWEyMmMwZjllYjgyNDQzNjgyNWNiN2VhYjQ3MGY4MzI"
											target="_blank"
											onClick={() => {
												fireUiEvent({
													action: 'clicked',
													actionSubject: 'link',
													actionSubjectId: 'heading-info-link',
													source: 'home-page',
												});
											}}
										>
											{formatMessage(messages.discoverMoreLinkLabel)}
										</Link>
									</Text>
								</Stack>
							</Box>

							{showIntegrationsIncompleteStatus && integrationsComponent.length > 0 && (
								<Box xcss={panelStyles}>
									<Stack space="space.200">
										<Box>
											<Flex alignItems="center" justifyContent="space-between">
												<Heading size="large">
													{formatMessage(messages.connectSCMToolsHeading)}
												</Heading>
												<Button
													appearance="default"
													onClick={() => setHideSetupSection(!hideSetupSection)}
													iconBefore={
														hideSetupSection ? HipchatChevronDownIcon : HipchatChevronUpIcon
													}
												>
													{hideSetupSection
														? formatMessage(messages.show)
														: formatMessage(messages.hide)}
												</Button>
											</Flex>
										</Box>
										{!hideSetupSection && (
											<>
												<Text as="p">{formatMessage(messages.connectSCMToolsDescription)}</Text>
												<Stack space="space.200">
													<Grid xcss={responsiveSCMToolsStyles} gap="space.200">
														{integrationsComponent}
													</Grid>
													<Text as="p">
														<Button appearance="subtle" onClick={() => setSkipSetupSection(true)}>
															{formatMessage(messages.skipConnectingForNow)}
														</Button>
													</Text>
												</Stack>
											</>
										)}
									</Stack>
								</Box>
							)}
						</Box>

						<Capabilities
							hasScmIntegrations={!showIntegrationsIncompleteStatus}
							integrationsConfigStatesFragment={data.jira?.devOps?.configStates?.edges}
						/>
						<Manage integrationsConfigStatesFragment={data.jira?.devOps?.configStates?.edges} />
						<Feedback />
					</Stack>
				</Box>
			</Flex>
		</Grid>
	);
};

export const HomePage = () => {
	const { tenantContexts } = useTenantData();
	const { rovoDevAgentsUser } = useUserPermData();
	const { cloudId } = tenantContexts[0];

	if (!rovoDevAgentsUser.hasProductAccess || !rovoDevAgentsUser.isAdmin) {
		return <NonProductAdminHomePage />;
	}

	return <MainContent cloudID={cloudId} />;
};

const panelStyles = xcss({
	width: '100%',
	backgroundColor: 'elevation.surface.overlay',
	padding: 'space.400',
	marginTop: 'space.400',
	borderRadius: 'border.radius.100',
	border: `1px solid ${token('color.border')}`,
});

const suggestionsWrapperStyles = xcss({
	paddingTop: 'space.500',
	paddingBottom: 'space.300',
});

const mainHeadingWrapperStyles = xcss({
	'::after': {
		content: '""',
		position: 'absolute',
		top: 'space.0',
		right: '-40px',
		width: '208px',
		height: '208px',
		background: `url(${rocketImage}) no-repeat`,
	},
	position: 'relative',
});

const setupHeadingWrapperStyles = xcss({
	'::after': {
		content: '""',
		position: 'absolute',
		top: 'space.0',
		right: '-100px',
		width: '208px',
		height: '208px',
		background: `url(${settingsImage}) no-repeat`,
	},
	position: 'relative',
});

const mainHeadingStyles = xcss({
	width: 'calc(100% - 250px)',
});

const responsiveSCMToolsStyles = xcss({
	[media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
	[media.above.md]: {
		gridTemplateColumns: 'repeat(2, 1fr)',
	},
});

const homeWrapperStyles = xcss({
	paddingTop: 'space.500',
	paddingBottom: 'space.300',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const homeDescriptionStyles = xcss({ marginRight: 'space.050' });

const responsiveStyles = xcss({
	[media.above.lg]: {
		width: '1200px',
	},
	marginLeft: 'auto',
	marginRight: 'auto',
});
